import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import ClientFicheModal from "@/components/ClientFicheModal";
import { toast } from "sonner";

export default function SendDocModal({ doc, kind, onClose, onSent }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [completeClient, setCompleteClient] = useState(null);

  const isInvoice = kind === "invoice";
  const isPaid = isInvoice && doc?.status === "payee";
  const title = isPaid ? "Facture acquittée" : isInvoice ? "Facture" : kind === "quote" ? "Devis" : "Avoir";
  const pdfEndpoint = isInvoice ? `/accounting/invoices/${doc?.id}/pdf`
    : kind === "quote" ? `/accounting/quotes/${doc?.id}/pdf`
    : `/accounting/credit-notes/${doc?.id}/pdf`;
  const sendEndpoint = isInvoice ? `/accounting/invoices/${doc?.id}/send`
    : kind === "quote" ? `/accounting/quotes/${doc?.id}/send`
    : `/accounting/credit-notes/${doc?.id}/send`;

  useEffect(() => {
    if (!doc) return;
    const clientName = doc.snapshot?.client?.name || doc.client_name || "";
    setTo(doc.snapshot?.client?.email || "");
    setSubject(isPaid
      ? `Facture acquittée N° ${doc.number || ""} — reçu de paiement`
      : `${title} N° ${doc.number || ""}${kind === "credit_note" ? ` (facture ${doc.invoice_number})` : ""}`.trim());
    const fallback = isPaid
      ? `Bonjour ${clientName},\n\nnous vous confirmons la bonne réception de votre règlement. Vous trouverez ci-joint votre facture acquittée N° ${doc.number || ""}.\n\nBien cordialement,`
      : `Bonjour ${clientName},\n\nvous trouverez votre ${title.toLowerCase()} en cliquant sur le bouton ci-dessous.\n\nBien cordialement,`;
    setMessage(fallback);
    if (!isPaid) api.get("/settings").then((r) => {
      const tpl = r.data.doc_email_template;
      if (tpl) {
        setMessage(tpl.replaceAll("{client}", clientName)
          .replaceAll("{numero}", doc.number || "")
          .replaceAll("{document}", title.toLowerCase()));
      }
    }).catch(() => {});
    if (!doc.snapshot?.client?.email && doc.client_id) {
      api.get(`/clients/${doc.client_id}`)
        .then((r) => setTo(r.data.billing_email || r.data.email || ""))
        .catch(() => {});
    }
    let obj;
    api.get(pdfEndpoint, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setPdfUrl(obj); })
      .catch(() => setPdfUrl(null));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [doc]);

  const submit = async () => {
    if (!to.trim()) { toast.error("Destinataire requis"); return; }
    setLoading(true);
    try {
      await api.post(sendEndpoint, { to: to.trim(), subject, message });
      toast.success(`${title} envoyé(e) à ${to.trim()}`);
      onSent();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail || "";
      if (typeof d === "string" && d.startsWith("MISSING_CLIENT_INFO") && doc?.client_id) {
        toast.error("Certaines informations nécessaires sont manquantes sur la fiche client.");
        const r = await api.get(`/clients/${doc.client_id}`).catch(() => null);
        if (r) setCompleteClient(r.data);
      } else {
        toast.error(d || "Échec de l'envoi");
      }
    }
    finally { setLoading(false); }
  };

  return (
    <>
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="send-doc-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Aperçu avant envoi — {title} {doc?.number}</DialogTitle>
        <div className="grid md:grid-cols-2 gap-4 mt-2 min-h-0 flex-1">
          <div className="bg-neutral-100 rounded-xl overflow-hidden border border-neutral-200 min-h-[420px]" data-testid="send-doc-preview">
            {pdfUrl
              ? <iframe src={pdfUrl} title="Aperçu PDF" className="w-full h-full min-h-[420px]" />
              : <div className="h-full flex items-center justify-center text-sm text-neutral-400">Chargement de l'aperçu…</div>}
          </div>
          <div className="space-y-3 overflow-y-auto">
            <div>
              <Label className="text-xs">Destinataire</Label>
              <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1" data-testid="send-doc-to" placeholder="email@client.com" />
            </div>
            <div>
              <Label className="text-xs">Objet</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="send-doc-subject" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" data-testid="send-doc-message" />
            </div>
            <Button onClick={submit} disabled={loading} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="send-doc-submit">
              <Send className="w-4 h-4" /> {loading ? "Envoi…" : "Valider et envoyer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    {completeClient && (
      <ClientFicheModal open={!!completeClient} client={completeClient}
        onClose={() => setCompleteClient(null)}
        onSaved={() => { setCompleteClient(null); submit(); }} />
    )}
    </>
  );
}
